import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Minus, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { COLORS, FLEET_TIERS, MAX_QTY, PACKS, isColorAvailable, packImage, packPrice, packCompareAt, savingsVsSingles, unitPriceFor, volumeTierFor } from "@/data/products";
import { usePlatform } from "@/context/PlatformContext";
import { useCountry } from "@/context/CountryContext";
import VolumeRewards from "@/components/VolumeRewards";
import ReviewSnippets from "@/components/ReviewSnippets";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";
import BundleAssetMap, { visibleAssetsFor } from "@/components/BundleAssetMap";
import TrackerPhone from "@/components/TrackerPhone";
import { SectionHeader } from "@/components/Primitives";

const PackCard = ({ pack, selected, onSelect, soldOut = false, platform = "apple", market, symbol }) => {
  const finishes = COLORS.filter((c) => isColorAvailable(c, pack.id));
  const price = packPrice(pack, market);
  const unitBaseline = unitPriceFor(market);
  // Savings-vs-singles in the current currency.
  const saving = pack.id * unitBaseline - price;
  const img = packImage("obsidian", pack.id, platform);
  return (
    <motion.button
      type="button"
      data-testid={`bundle-option-${pack.id}pack`}
      onClick={() => onSelect(pack.id)}
      disabled={soldOut}
      whileHover={soldOut ? undefined : { y: -3 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className={`relative flex h-full flex-col rounded-3xl border-2 bg-white p-5 text-left transition-shadow duration-300 ${
        soldOut
          ? "cursor-not-allowed border-black/8 opacity-55"
          : selected
            ? "border-tracksy-orange shadow-glow"
            : "border-black/8 shadow-subtle hover:shadow-lift"
      }`}
    >
      {soldOut && (
        <span className="absolute -top-3 left-5 whitespace-nowrap rounded-full bg-tracksy-ink px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-white">
          Sold out
        </span>
      )}
      {pack.freeBonus && !soldOut && (
        <span
          data-testid={`free-bonus-badge-${pack.id}`}
          className="absolute -top-3 right-4 whitespace-nowrap rounded-full bg-tracksy-green px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-white shadow-lift"
        >
          +{pack.freeBonus} FREE
        </span>
      )}
      {pack.recommended && !soldOut && (
        <span className="absolute -top-3 left-5 whitespace-nowrap rounded-full bg-tracksy-orange px-3 py-1 text-[10px] font-extrabold uppercase tracking-widest text-white">
          Recommended
        </span>
      )}
      <span className={`absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${selected && !soldOut ? "border-tracksy-orange bg-tracksy-orange" : "border-black/15"}`}>
        {selected && !soldOut && <Check className="h-3.5 w-3.5 text-white" />}
      </span>
      {/* Tracker-count thumbnail — transparent pucks floating above the card, no box, no border */}
      <div className="relative mx-auto mb-3 flex h-20 w-full items-center justify-center">
        <img
          src={img}
          alt={`${pack.label} — ${pack.id} Tracksy tracker${pack.id > 1 ? "s" : ""}`}
          loading="lazy"
          className="h-full w-full object-contain drop-shadow-[0_8px_14px_rgba(20,16,10,0.18)]"
        />
        {/* Floating free Travel Card — only on 4-Pack and 8-Pack, same visual
            treatment as the bundle hero (multiply-blended png, no backdrop,
            no border, green FREE pill below). */}
        {pack.freeBonus && !soldOut && (
          <div
            data-testid={`pack-free-travelcard-${pack.id}`}
            className="pointer-events-none absolute -right-1 bottom-0 flex flex-col items-center gap-0.5"
          >
            <img
              src="/images/travel-card.webp"
              alt="Free Tracksy Travel Card included"
              loading="lazy"
              className="h-14 w-auto object-contain"
              style={{ mixBlendMode: "multiply", transform: "rotate(8deg)" }}
            />
            <span className="whitespace-nowrap rounded-full bg-tracksy-green px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest text-white shadow-sm ring-1 ring-white">
              + Card Free
            </span>
          </div>
        )}
      </div>
      <p className="text-xs font-bold uppercase tracking-widest text-tracksy-sub">{pack.label}</p>
      <p className="mt-0.5 font-heading text-sm font-bold text-tracksy-ink">{pack.name}</p>
      {/* Price block — compare-at strike + fat discount badge on multi-packs so
          the "15% off" / "20% off" reads loud at the moment of decision. */}
      {(() => {
        const compareAt = packCompareAt(pack, market);
        const hasDiscount = compareAt && compareAt > price && pack.discountPct;
        return (
          <>
            <div className="mt-3 flex items-baseline gap-2 flex-wrap">
              <p className="font-heading text-3xl font-extrabold tracking-tight text-tracksy-ink">{symbol}{price}</p>
              {hasDiscount && (
                <>
                  <span data-testid={`compare-at-${pack.id}`} className="font-heading text-base font-bold text-tracksy-sub line-through decoration-tracksy-sub/70">
                    {symbol}{compareAt}
                  </span>
                  <span
                    data-testid={`discount-badge-${pack.id}`}
                    className="inline-flex items-center rounded-md bg-tracksy-orange px-2 py-0.5 font-heading text-sm font-black uppercase leading-none tracking-wide text-white shadow-sm"
                  >
                    −{pack.discountPct}%
                  </span>
                </>
              )}
            </div>
            <p className="mt-1 text-xs text-tracksy-sub">{symbol}{(price / pack.id).toFixed(2)} per tracker · {soldOut ? "Currently unavailable" : pack.tagline}</p>
          </>
        );
      })()}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {pack.freeBonus && !soldOut && (
          <span data-testid={`free-bonus-chip-${pack.id}`} className="inline-flex items-center gap-1 rounded-full bg-tracksy-green/15 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-tracksy-green">
            Ships {pack.id + pack.freeBonus} units + 1 Travel Card
          </span>
        )}
        {saving > 0 && !soldOut && (
          <span className="rounded-full bg-tracksy-orange/10 px-3 py-1 text-xs font-bold text-tracksy-orange">Save {symbol}{saving} vs singles</span>
        )}
        <span className="flex items-center gap-1" aria-label={finishes.map((f) => f.name).join(", ")}>
          {finishes.map((f) => (
            <span key={f.id} className="h-3.5 w-3.5 rounded-full border border-black/15" style={{ backgroundColor: f.hex }} />
          ))}
        </span>
      </div>
    </motion.button>
  );
};

const FleetTierCard = ({ tier, label }) => (
  <Link
    to="/fleet#enquiry"
    data-testid={`bundle-fleet-${tier.id}`}
    className="group flex h-full flex-col rounded-3xl border-2 border-dashed border-black/15 bg-tracksy-cream p-5 transition-colors hover:border-tracksy-ink"
  >
    <p className="text-xs font-bold uppercase tracking-widest text-tracksy-sub">{label}</p>
    <p className="mt-0.5 font-heading text-sm font-bold text-tracksy-ink">{tier.name}</p>
    <p className="mt-3 font-heading text-xl font-extrabold tracking-tight text-tracksy-ink">Tailored pricing</p>
    <p className="mt-1 text-xs text-tracksy-sub">{tier.range} · enterprise, resellers &amp; multi-site</p>
    <span className="mt-auto inline-flex items-center gap-1 pt-4 text-xs font-bold text-tracksy-orange">
      Request fleet pricing <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
    </span>
  </Link>
);

const BundleShop = ({ sticky = false, title = "Buy one. Or equip everything that moves.", eyebrow = "Bundles", showVolumeRewards = true }) => {
  const { platform, config } = usePlatform();
  const { market, currency, symbol } = useCountry();
  const isUSD = currency === "USD";
  // Android line: only 4-Pack and 8-Pack are available for now.
  const androidPackAvailable = (id) => id === 4 || id === 8;
  const packDisabled = (id) => platform === "android" && !androidPackAvailable(id);

  const [packId, setPackId] = useState(platform === "android" ? 4 : 4);
  const [colorId, setColorId] = useState("obsidian");
  const [qty, setQty] = useState(1);
  // Shared selection between the map pin and the phone row — tapping either
  // highlights the matching asset in both places.
  const initialVisible = visibleAssetsFor(packId);
  const [selectedPinId, setSelectedPinId] = useState(initialVisible[0]?.id ?? null);
  const { addItem } = useCart();

  // When the user flips to Android while a sold-out pack is selected, snap to 4-Pack.
  useEffect(() => {
    if (platform === "android" && !androidPackAvailable(packId)) {
      setPackId(4);
    }
  }, [platform, packId]);

  // Keep the shared selection valid as the pack size (and therefore the number
  // of visible assets) changes.
  const visibleAssets = visibleAssetsFor(packId);
  useEffect(() => {
    if (!selectedPinId || !visibleAssets.some((v) => v.id === selectedPinId)) {
      setSelectedPinId(visibleAssets[0]?.id ?? null);
    }
  }, [packId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pack = PACKS.find((p) => p.id === packId);
  const color = COLORS.find((c) => c.id === colorId);
  const image = packImage(colorId, packId, platform);
  const trackers = pack.id * qty;
  const tier = volumeTierFor(trackers);
  const packUnitPrice = packPrice(pack, market);
  const gross = packUnitPrice * qty;
  const net = Math.round(gross * (1 - (tier?.discount || 0)));

  const selectPack = (id) => {
    if (packDisabled(id)) {
      toast.info(`${PACKS.find((p) => p.id === id).label} is sold out for the Android line`, {
        description: "Please pick the 4-Pack or 8-Pack.",
      });
      return;
    }
    setPackId(id);
    if (!isColorAvailable(color, id)) {
      const fallback = COLORS.find((c) => isColorAvailable(c, id));
      setColorId(fallback.id);
      toast.info(`${color.name} is sold out in the ${PACKS.find((p) => p.id === id).label} pack`, { description: `Switched to ${fallback.name}.` });
    }
  };

  const selectColor = (c) => {
    if (!isColorAvailable(c, packId)) {
      toast.error(`${c.name} is sold out in the ${pack.label} pack`, { description: "Currently available in the 4-pack and 8-pack." });
      return;
    }
    setColorId(c.id);
  };

  const handleAdd = () => {
    addItem({ key: `${pack.id}-${color.id}`, packId: pack.id, name: `${pack.label} · ${pack.name}`, color: color.name, colorHex: color.hex, image, price: packUnitPrice, qty });
    toast.success(`${pack.label} added to cart`, { description: `${pack.id} tracker${pack.id > 1 ? "s" : ""} · ${color.name}` });
  };

  return (
    <section id="bundles" data-testid="bundle-shop-section" className="scroll-mt-24 px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <SectionHeader eyebrow={eyebrow} title={title} desc="The 4-pack is our recommended starting point for most households — a vehicle, a trailer, a toolbox and a bike. Businesses can order any quantity online; for 100+ devices, request tailored pricing." className="mb-12" />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          {/* ─── LEFT: Hero card — pucks (top-left), map (bottom-left), realistic phone floats to the right INSIDE the same card ─── */}
          <Reveal className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div
                data-testid="bundle-hero-card"
                className={`relative rounded-[2.5rem] shadow-subtle ${
                  platform === "android"
                    ? "bg-gradient-to-b from-[#EAF0F8] via-[#E4EDF7] to-[#DCE6F2]"
                    : "bg-gradient-to-b from-[#F7ECD3] via-[#F2E7CE] to-[#EBE0C5]"
                }`}
              >
                {/* Phone — floats on the right side of the hero card, sharing space with the pucks and map.
                    Hidden on mobile (stacked below instead) so it never cramps the pucks. */}
                <div className="pointer-events-none absolute right-4 top-1/2 z-20 hidden w-[44%] -translate-y-1/2 sm:block">
                  <motion.div
                    initial={{ opacity: 0, y: 20, rotate: -4 }}
                    whileInView={{ opacity: 1, y: 0, rotate: -2 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="pointer-events-auto relative"
                  >
                    <TrackerPhone
                      items={visibleAssets}
                      selectedId={selectedPinId}
                      onSelect={setSelectedPinId}
                      accent={config.accent}
                      platformShort={config.short || "Tracksy"}
                      testIdSuffix="desktop"
                    />
                  </motion.div>
                </div>

                {/* LEFT STACK: pills row → pucks → map. Pills are in normal
                    document flow (not absolute), so they can never sit on
                    top of the pucks regardless of pack size or viewport. */}
                <div className="sm:pr-[44%]">
                  {/* Pills row — reserved space at the top of the card. */}
                  <div
                    data-testid="bundle-hero-pills"
                    className="flex flex-wrap items-center gap-2 px-6 pt-6"
                  >
                    {(packId === 4 || packId === 8) && (
                      <motion.span
                        key={`free-tc-pill-${packId}`}
                        data-testid="bundle-free-travelcard"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex whitespace-nowrap items-center gap-1 rounded-full bg-tracksy-green px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-white shadow-md ring-2 ring-white"
                      >
                        + Travel Card Free
                      </motion.span>
                    )}
                    <span className="whitespace-nowrap rounded-full border border-black/5 bg-white/85 px-3 py-1.5 text-[11px] font-bold text-tracksy-ink backdrop-blur-xl">
                      {pack.label} · {color.name}
                    </span>
                    <span className="whitespace-nowrap rounded-full bg-tracksy-ink/90 px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-[0.18em] text-white">
                      Magnetic · Compact · Versatile
                    </span>
                  </div>

                  {/* Puck zone — pucks get their own clean space below the pills */}
                  <div className="relative flex aspect-[5/4] w-full items-center justify-center overflow-hidden rounded-t-[2.5rem] px-6 pt-2 pb-6 sm:aspect-[5/4]">
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.img
                        key={image}
                        data-testid="bundle-product-image"
                        src={image}
                        alt={`Tracksy ${pack.label} in ${color.name}`}
                        initial={{ opacity: 0, y: 10, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.98 }}
                        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        className="max-h-full w-auto max-w-[82%] object-contain drop-shadow-[0_28px_30px_rgba(20,14,6,0.22)]"
                      />
                    </AnimatePresence>
                  </div>

                  {/* Hairline divider */}
                  <div className="pointer-events-none mx-6 h-px bg-gradient-to-r from-transparent via-black/10 to-transparent" />

                  {/* Map zone */}
                  <div className="relative h-[240px] w-full overflow-hidden rounded-b-[2.5rem] p-4 sm:h-[280px] sm:rounded-bl-[2.5rem] sm:rounded-br-none">
                    <BundleAssetMap
                      packId={packId}
                      embedded
                      selectedId={selectedPinId}
                      onSelectId={setSelectedPinId}
                    />
                  </div>
                </div>

                {/* Mobile-only phone (stacked below the map so small screens still get the interaction) */}
                <div className="px-6 pb-6 pt-4 sm:hidden">
                  <div className="mx-auto max-w-[260px]">
                    <TrackerPhone
                      items={visibleAssets}
                      selectedId={selectedPinId}
                      onSelect={setSelectedPinId}
                      accent={config.accent}
                      platformShort={config.short || "Tracksy"}
                      testIdSuffix="mobile"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* ─── RIGHT: Pack grid + options + volume rewards ─── */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2" data-testid="bundle-grid">
              {PACKS.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <PackCard pack={p} selected={packId === p.id} onSelect={selectPack} soldOut={packDisabled(p.id)} platform={platform} market={market} symbol={symbol} />
                </Reveal>
              ))}
            </div>

            <AnimatePresence>
              {packId === 1 && (
                <motion.div
                  key="nudge"
                  data-testid="single-pack-nudge"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-tracksy-orange/30 bg-tracksy-orange/5 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm font-semibold text-tracksy-ink">Need to track more than one vehicle or asset?</p>
                    <div className="flex gap-2">
                      {[2, 4].map((id) => {
                        const p = PACKS.find((x) => x.id === id);
                        return (
                          <button key={id} type="button" data-testid={`nudge-pack-${id}`} onClick={() => selectPack(id)} className="rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-bold text-tracksy-ink transition-colors hover:border-tracksy-orange hover:text-tracksy-orange">
                            {p.label} — {symbol}{packPrice(p, market)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ─── Buy block: Price + Qty + Add to Cart sits directly beneath the pack grid
                 so mobile shoppers stop dead-clicking the pack tiles expecting the CTA. ─── */}
            <Reveal delay={0.15}>
              <div className="mt-6 rounded-3xl border border-black/5 bg-white p-6 shadow-subtle">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-tracksy-sub">{pack.label} · {pack.name} · {color.name}</p>
                    <p className="flex items-baseline gap-2">
                      <span data-testid="bundle-total-price" className="font-heading text-3xl font-extrabold tracking-tight text-tracksy-ink">{symbol}{net}</span>
                      {tier && <span data-testid="bundle-gross-price" className="text-sm font-semibold text-tracksy-muted line-through">{symbol}{gross}</span>}
                      {tier && <span data-testid="bundle-tier-badge" className="rounded-full bg-tracksy-orange/10 px-2.5 py-0.5 text-xs font-bold text-tracksy-orange">{tier.name} · {tier.headline}</span>}
                    </p>
                    <p data-testid="bundle-tracker-count" className="text-xs font-semibold text-tracksy-sub">{trackers} tracker{trackers === 1 ? "" : "s"}{tier?.freeTrackers ? <span className="text-tracksy-green"> + {tier.freeTrackers} free</span> : null} · ${(net / (trackers + (tier?.freeTrackers || 0))).toFixed(2)} each</p>
                  </div>
                  <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                    <div className="inline-flex items-center justify-between gap-3 self-stretch rounded-full border border-black/10 px-2 py-1.5 sm:self-auto">
                      <button type="button" data-testid="qty-decrease-button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-10 w-10 items-center justify-center rounded-full text-tracksy-sub hover:bg-tracksy-cream hover:text-tracksy-ink sm:h-8 sm:w-8" aria-label="Decrease quantity"><Minus className="h-4 w-4" /></button>
                      <input
                        type="number"
                        min={1}
                        max={MAX_QTY}
                        data-testid="qty-value"
                        value={qty}
                        onChange={(e) => setQty(Math.min(MAX_QTY, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                        aria-label="Quantity"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="w-10 bg-transparent text-center text-base font-bold text-tracksy-ink focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none sm:text-sm"
                      />
                      <button type="button" data-testid="qty-increase-button" onClick={() => setQty((q) => Math.min(MAX_QTY, q + 1))} className="flex h-10 w-10 items-center justify-center rounded-full text-tracksy-sub hover:bg-tracksy-cream hover:text-tracksy-ink sm:h-8 sm:w-8" aria-label="Increase quantity"><Plus className="h-4 w-4" /></button>
                    </div>
                    <motion.button type="button" data-testid="add-to-cart-button" onClick={handleAdd} whileTap={{ scale: 0.96 }} className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-tracksy-orange px-6 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-glow transition-colors hover:bg-tracksy-orangeHover sm:w-auto">
                      <ShoppingBag className="h-5 w-5" /> Add to Cart
                    </motion.button>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* ─── Finish (colour) — moved below Add to Cart per UX request. ─── */}
            <Reveal delay={0.17}>
              <div className="mt-4 rounded-3xl border border-black/5 bg-white p-6 shadow-subtle" data-testid="bundle-finish-card">
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-tracksy-sub">Finish</p>
                <div className="flex items-center gap-3">
                  {COLORS.map((c) => {
                    const available = isColorAvailable(c, packId);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`color-option-${c.id}`}
                        data-available={available}
                        onClick={() => selectColor(c)}
                        aria-label={available ? c.name : `${c.name} — sold out`}
                        title={available ? c.name : `${c.name} — sold out`}
                        className={`relative h-11 w-11 rounded-full border-2 transition-transform duration-200 hover:scale-110 sm:h-9 sm:w-9 ${colorId === c.id ? "ring-2 ring-tracksy-orange ring-offset-2" : ""} ${available ? "" : "opacity-60"}`}
                        style={{ backgroundColor: c.hex, borderColor: "rgba(0,0,0,0.12)" }}
                      >
                        {!available && <span className="absolute left-1/2 top-1/2 h-0.5 w-10 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded bg-tracksy-ink/70" />}
                      </button>
                    );
                  })}
                </div>
                <p data-testid="selected-color-label" className="mt-2 text-xs font-semibold text-tracksy-ink">{color.name}</p>
                {!isColorAvailable(COLORS[1], packId) && (
                  <p data-testid="white-sold-out-note" className="mt-0.5 text-xs font-medium text-tracksy-muted">Ceramic White sold out in this pack</p>
                )}
                <p className="mt-4 text-xs leading-relaxed text-tracksy-muted">Each tracker includes a magnetic protective case and two metal mounting sheets. Replaceable CR2025 battery. Works with {platform === "android" ? "any Android phone and the Google Find Hub app" : "iPhone and the Apple Find My app"}.</p>
              </div>
            </Reveal>

            {/* ─── Fleet 100+ — moved out of the pack grid so it no longer competes with Add to Cart. ─── */}
            <Reveal delay={0.19}>
              <div className="mt-4" data-testid="bundle-fleet-tile">
                <FleetTierCard tier={FLEET_TIERS[3]} label="100+ Tracksy" />
              </div>
            </Reveal>

            <Reveal delay={0.21}>
              {showVolumeRewards ? (
                <div className="mt-8" data-testid="bundle-volume-rewards">
                  <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-widest text-tracksy-orange">Volume rewards</p>
                      <p className="font-heading text-lg font-bold text-tracksy-ink">The more you equip, the more you get.</p>
                    </div>
                    <p className="text-xs text-tracksy-sub">Discounts apply automatically in your cart, based on total trackers.</p>
                  </div>
                  <VolumeRewards count={trackers} />
                </div>
              ) : null}
            </Reveal>

            <Reveal delay={0.23}>
              <ReviewSnippets />
            </Reveal>
          </div>
        </div>
      </div>

      {sticky && (
        <div data-testid="sticky-add-to-cart" className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-black/5 bg-white/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-xl lg:hidden">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-tracksy-sub">{pack.label} · {color.name}</p>
            <p className="font-heading text-xl font-extrabold text-tracksy-ink">${net}</p>
          </div>
          <button type="button" data-testid="sticky-add-to-cart-button" onClick={handleAdd} className="inline-flex items-center gap-2 rounded-full bg-tracksy-orange px-6 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-glow">
            <ShoppingBag className="h-4 w-4" /> Add to cart
          </button>
        </div>
      )}
    </section>
  );
};

export default BundleShop;
